import type { Ref } from "react";
import { useTranslation } from "react-i18next";

import { RelativeTime } from "@/components/ui/v2/relative-time";
import { TruncatedTooltip } from "@/components/ui/v2/truncated-tooltip";
import { SegmentStatusDot } from "@/features/segments/components/SegmentStatusDot";
import type { SegmentStatus } from "@/features/segments/constants";
import { cn } from "@/lib/utils/cn";

type SegmentCardProps = {
  name: string;
  status: SegmentStatus;
  lastUpdatedDate?: string;
  isSelected: boolean;
  onClick: () => void;
  cardRef?: Ref<HTMLButtonElement>;
  statusRef?: Ref<HTMLSpanElement>;
};

export function SegmentCard({
  name,
  status,
  lastUpdatedDate,
  isSelected,
  onClick,
  cardRef,
  statusRef,
}: SegmentCardProps) {
  const { t } = useTranslation();

  return (
    <button
      ref={cardRef}
      type="button"
      data-testid="segment-card"
      data-slot="panel"
      onClick={onClick}
      className={cn(
        "w-full cursor-pointer rounded-lg border border-border bg-card p-4 text-left transition-colors",
        "hover:brightness-110",
        isSelected && "border-primary",
      )}
    >
      <div className="flex items-start gap-3">
        <SegmentStatusDot status={status} className="mt-2" dotRef={statusRef} />
        <div className="min-w-0">
          <TruncatedTooltip text={name}>
            <p className="truncate text-sm font-semibold">{name}</p>
          </TruncatedTooltip>
          {lastUpdatedDate && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("common.lastUpdated")} <RelativeTime date={lastUpdatedDate} />
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
